import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Download, 
  Navigation, 
  Shield, 
  AlertTriangle,
  Clock,
  Users,
  MapPin,
  FileText,
  Zap
} from 'lucide-react';
import EmergencyContactsModal from './EmergencyContactsModal';
import DownloadGuidesModal from './DownloadGuidesModal';
import EvacuationRoutesModal from './EvacuationRoutesModal';

interface EmergencyFeaturesHubProps {
  isOpen: boolean;
  onClose: () => void;
}

const EmergencyFeaturesHub: React.FC<EmergencyFeaturesHubProps> = ({ isOpen, onClose }) => {
  const [activeModal, setActiveModal] = useState<'contacts' | 'guides' | 'routes' | null>(null);

  const features = [
    {
      id: 'contacts',
      title: 'Emergency Contacts',
      description: 'Quick access to emergency services and personal contacts',
      icon: Phone,
      color: 'from-accent-red to-accent-orange',
      bgColor: 'bg-accent-red/10',
      borderColor: 'border-accent-red/30',
      features: [
        'One-click calling & SMS',
        'Voice command support',
        'Personal contact management',
        'Offline access'
      ]
    },
    {
      id: 'guides',
      title: 'Download Safety Guides',
      description: 'Generate customized emergency preparedness guides',
      icon: Download,
      color: 'from-primary-400 to-primary-500',
      bgColor: 'bg-primary-400/10',
      borderColor: 'border-primary-400/30',
      features: [
        'Customizable PDF guides',
        'Multiple formats available',
        'Offline accessibility',
        'Print-optimized'
      ]
    },
    {
      id: 'routes',
      title: 'Evacuation Routes',
      description: 'Find safe zones and optimal evacuation paths',
      icon: Navigation,
      color: 'from-accent-orange to-accent-yellow',
      bgColor: 'bg-accent-orange/10',
      borderColor: 'border-accent-orange/30',
      features: [
        'Real-time route planning',
        'Multiple transport modes',
        'Safe zone information',
        'Turn-by-turn navigation'
      ]
    }
  ];

  const openFeature = (featureId: string) => {
    setActiveModal(featureId as any);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  if (!isOpen && !activeModal) return null;

  return (
    <>
      {/* Main Hub Modal */}
      {isOpen && !activeModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            className="bg-dark-800 border border-dark-600 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-dark-700">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-accent-red to-accent-orange rounded-lg flex items-center justify-center">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">Emergency Features</h1>
                  <p className="text-gray-400">Critical tools for emergency preparedness and response</p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-dark-700/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-accent-red mb-1">24/7</div>
                  <div className="text-xs text-gray-400">Emergency Access</div>
                </div>
                <div className="bg-dark-700/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-primary-400 mb-1">3</div>
                  <div className="text-xs text-gray-400">Core Features</div>
                </div>
                <div className="bg-dark-700/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-accent-green mb-1">100%</div>
                  <div className="text-xs text-gray-400">Offline Ready</div>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div className="p-6">
              <div className="grid md:grid-cols-1 gap-6">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <motion.div
                      key={feature.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`border rounded-xl p-6 hover:border-opacity-50 transition-all duration-300 cursor-pointer group ${feature.borderColor} ${feature.bgColor}`}
                      onClick={() => openFeature(feature.id)}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-primary-300 transition-colors">
                            {feature.title}
                          </h3>
                          <p className="text-gray-300 mb-4">{feature.description}</p>
                          
                          <div className="grid grid-cols-2 gap-2">
                            {feature.features.map((item, itemIndex) => (
                              <div key={itemIndex} className="flex items-center space-x-2 text-sm text-gray-400">
                                <div className="w-1.5 h-1.5 bg-primary-400 rounded-full"></div>
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="text-right">
                          <motion.div
                            whileHover={{ x: 5 }}
                            className="w-8 h-8 bg-dark-600 rounded-lg flex items-center justify-center group-hover:bg-primary-400 transition-colors"
                          >
                            <span className="text-white">→</span>
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Emergency Notice */}
              <div className="mt-8 bg-gradient-to-r from-accent-red/10 to-accent-orange/10 border border-accent-red/30 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-accent-red" />
                  <h3 className="text-lg font-semibold text-white">Emergency Preparedness Tips</h3>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-accent-orange">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">Before Emergency</span>
                    </div>
                    <ul className="text-gray-300 space-y-1 text-xs">
                      <li>• Download and print safety guides</li>
                      <li>• Set up emergency contacts</li>
                      <li>• Plan evacuation routes</li>
                      <li>• Prepare emergency kit</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-accent-red">
                      <Zap className="w-4 h-4" />
                      <span className="font-medium">During Emergency</span>
                    </div>
                    <ul className="text-gray-300 space-y-1 text-xs">
                      <li>• Stay calm and follow protocols</li>
                      <li>• Use emergency contacts</li>
                      <li>• Follow evacuation routes</li>
                      <li>• Keep family informed</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-accent-green">
                      <Users className="w-4 h-4" />
                      <span className="font-medium">After Emergency</span>
                    </div>
                    <ul className="text-gray-300 space-y-1 text-xs">
                      <li>• Check on family and neighbors</li>
                      <li>• Report to authorities if safe</li>
                      <li>• Document any damage</li>
                      <li>• Seek assistance if needed</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Quick Access */}
              <div className="mt-6 flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                  className="px-8 py-3 bg-dark-700 hover:bg-dark-600 border border-dark-600 rounded-lg font-medium transition-colors"
                >
                  Close
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Feature Modals */}
      <EmergencyContactsModal
        isOpen={activeModal === 'contacts'}
        onClose={closeModal}
      />
      
      <DownloadGuidesModal
        isOpen={activeModal === 'guides'}
        onClose={closeModal}
      />
      
      <EvacuationRoutesModal
        isOpen={activeModal === 'routes'}
        onClose={closeModal}
      />
    </>
  );
};

export default EmergencyFeaturesHub;